/**
 * BagBadge — muestra la info de bolsa WH para una orden ML.
 *
 * Variantes:
 *   - compact: pill chiquito (1 línea) para listas/tablas
 *   - full:    bloque destacado con borde azul (para detail views)
 *
 * Si la orden no tiene bolsa (orden vieja o aún no procesada en depósito):
 *   - compact: no renderiza nada
 *   - full:    bloque gris explicativo
 *
 * Caso "sin bolsa intencional" (voluminoso, caja propia):
 *   - compact: pill naranja "📦 Sin bolsa (voluminoso)"
 *   - full:    bloque naranja con detalle
 */
import { useBagInfo, type BagInfo, type NoBagInfo } from '../hooks/useBagInfo';

interface Props {
  mlOrderId: number | string;
  sellerId: number;
  variant?: 'compact' | 'full';
}

function isNoBag(b: BagInfo | NoBagInfo): b is NoBagInfo {
  return (b as NoBagInfo).kind === 'no_bag';
}

export function BagBadge({ mlOrderId, sellerId, variant = 'compact' }: Props) {
  const { bag, isLoading } = useBagInfo(mlOrderId, sellerId);

  if (isLoading) return null;

  if (!bag) {
    if (variant === 'full') {
      return (
        <div className="rounded border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-600">
          📍 Sin info de bolsa (orden anterior al sistema de bolsas o aún no procesada en depósito)
        </div>
      );
    }
    return null;
  }

  // Caso "sin bolsa intencional" (voluminoso)
  if (isNoBag(bag)) {
    if (variant === 'compact') {
      return (
        <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs bg-orange-100 text-orange-800">
          📦 Sin bolsa ({bag.no_bag_reason}) · Batch #{bag.batch_id}
        </span>
      );
    }
    return (
      <div className="rounded border-l-4 border-orange-500 bg-orange-50 px-4 py-3 text-sm">
        <div className="font-semibold text-orange-900">📦 SIN BOLSA — caja propia</div>
        <div className="mt-1 text-gray-800">
          Producto <strong>{bag.no_bag_reason}</strong>, va en caja individual al despacho.
        </div>
        <div className="mt-1 text-gray-600">
          Batch #{bag.batch_id} del {new Date(bag.batch_date).toLocaleDateString('es')}
        </div>
      </div>
    );
  }

  const dispatched = !!bag.batch_released_at;
  const statusLabel = dispatched
    ? `despachada ${new Date(bag.batch_released_at!).toLocaleDateString('es')}`
    : bag.status === 'open'
    ? 'abierta'
    : 'cerrada';
  const statusColor = dispatched
    ? 'bg-blue-100 text-blue-800'
    : bag.status === 'open'
    ? 'bg-green-100 text-green-800'
    : 'bg-gray-200 text-gray-700';

  if (variant === 'compact') {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs ${statusColor}`}
      >
        🏷️ Batch #{bag.batch_id} · {bag.bag_code}
        {bag.courier ? ` · ${bag.courier.name}` : ''} ({statusLabel})
      </span>
    );
  }

  return (
    <div className="rounded border-l-4 border-blue-500 bg-blue-50 px-4 py-3 text-sm">
      <div className="font-semibold text-blue-900">📍 UBICACIÓN FÍSICA</div>
      <div className="mt-1 text-gray-800">
        Batch #{bag.batch_id} del {new Date(bag.batch_date).toLocaleDateString('es')} ·
        Bolsa <strong>{bag.bag_code}</strong>
        {bag.courier && (
          <span
            className="ml-2 rounded px-2 py-0.5 text-xs font-semibold text-white"
            style={{ backgroundColor: bag.courier.color }}
          >
            {bag.courier.name}
          </span>
        )}
      </div>
      <div className="mt-1 text-gray-600">
        Estado: {statusLabel}
        {dispatched && ' · 🚚 con courier'}
      </div>
    </div>
  );
}
